import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const inventoryArticleKeys = {
  all: ['inventoryArticles'],
  lists: () => [...inventoryArticleKeys.all, 'list'],
  list: (filters = {}) => [...inventoryArticleKeys.lists(), filters],
  details: () => [...inventoryArticleKeys.all, 'detail'],
  detail: (id) => [...inventoryArticleKeys.details(), id],
  search: (query) => [...inventoryArticleKeys.all, 'search', query],
};

const buildListParams = (filters = {}) => {
  const params = {};

  if (filters.search) params.search = filters.search;
  if (filters.status) params.status = filters.status;
  if (filters.page) params.page = filters.page;
  if (filters.per_page) params.per_page = filters.per_page;

  return params;
};

export const useInventoryArticles = (filters = {}, options = {}) => {
  const params = buildListParams(filters);

  return useQuery({
    queryKey: inventoryArticleKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get('inventory_articles', { params });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

export const useInventoryArticle = (id, options = {}) => {
  return useQuery({
    queryKey: inventoryArticleKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`inventory_articles/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useSearchInventoryArticles = (query, options = {}) => {
  return useQuery({
    queryKey: inventoryArticleKeys.search(query),
    queryFn: async () => {
      const response = await apiClient.get('inventory_articles/search', {
        params: { query },
      });
      return response.data;
    },
    enabled: !!query?.trim(),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

export const useCreateInventoryArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (articleData) => {
      const response = await apiClient.post('inventory_articles', articleData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryArticleKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating inventory article:', error);
    },
  });
};

export const useUpdateInventoryArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`inventory_articles/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(inventoryArticleKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: inventoryArticleKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating inventory article:', error);
    },
  });
};

export const useDeleteInventoryArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`inventory_articles/${id}`);
      return response.data;
    },
    onSuccess: (_data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: inventoryArticleKeys.lists() });
      queryClient.removeQueries({ queryKey: inventoryArticleKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting inventory article:', error);
    },
  });
};
